package com.example.socialmediaapp.network

import com.example.socialmediaapp.data.models.Message
import io.ktor.client.*
import io.ktor.client.plugins.websocket.*
import io.ktor.websocket.*
import kotlinx.coroutines.*
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

// Auth message — server expects JWT token, not userId
@Serializable
private data class WsAuth(val type: String, val token: String)

// Nested message object inside NEW_MESSAGE
@Serializable
private data class WsMsgObject(
    val id: String = "",
    @SerialName("chat_id") val chatId: String = "",
    @SerialName("sender_id") val senderId: String = "",
    @SerialName("sender_name") val senderName: String? = null,
    val content: String = "",
    val timestamp: Long = 0L
)

// Incoming WS event envelope
@Serializable
private data class WsEvent(
    val type: String = "",
    val message: WsMsgObject? = null  // used for NEW_MESSAGE
)

class ChatWebSocketClient(private val chatRepository: ChatRepository) {

    private val json = Json { ignoreUnknownKeys = true; isLenient = true; coerceInputValues = true }
    private val wsClient = HttpClient { install(WebSockets) }
    private var job: Job? = null

    fun connect(token: String, scope: CoroutineScope) {
        if (token.isBlank()) return
        job?.cancel()
        job = scope.launch {
            while (isActive) {
                try {
                    wsClient.webSocket(NetworkConfig.WS_URL) {
                        // Server verifies JWT token on AUTH
                        send(Frame.Text(json.encodeToString(WsAuth("AUTH", token))))
                        for (frame in incoming) {
                            if (frame is Frame.Text) handleMessage(frame.readText())
                        }
                    }
                } catch (e: Exception) {
                    delay(3000) // Reconnect after 3s
                }
            }
        }
    }

    private fun handleMessage(text: String) {
        try {
            val event = json.decodeFromString<WsEvent>(text)
            if (event.type == "NEW_MESSAGE") {
                val m = event.message ?: return
                chatRepository.onIncomingMessage(
                    Message(
                        id = m.id,
                        chatId = m.chatId,
                        senderId = m.senderId,
                        senderName = m.senderName,
                        content = m.content,
                        timestamp = m.timestamp * 1000L // server uses seconds
                    )
                )
            }
        } catch (_: Exception) {}
    }

    fun disconnect() {
        job?.cancel()
        job = null
        wsClient.close()
    }
}
